export const MOBILE_BOTTOM_DOCK_THRESHOLDS = {
	compactAfterDownPx: 24,
	expandAfterUpPx: 16,
	miniSwipeUpPx: 40,
	// now-playing drag-to-dismiss: a sustained downward drag beyond
	// nowPlayingDismissPx (or a downward fling) dismisses the panel; below
	// that it snaps back. nowPlayingSettleMs is the CSS transition window
	// (--duration-normal = 250ms) plus a small buffer used as a
	// transitionend fallback so the state swap still lands if no transition
	// fires (reduced motion, hidden surface, jsdom).
	nowPlayingDismissPx: 140,
	nowPlayingFlingPxPerMs: 0.35,
	nowPlayingSettleMs: 260,
} as const;

export type MobileBottomDockState = 'expanded' | 'compact' | 'now-playing';

export interface MobileBottomDockScrollExclusions {
	modalActive: boolean;
	editableFocused: boolean;
}

export interface MobileBottomDockSnapshot {
	dockState: MobileBottomDockState;
	hidden: boolean;
	scrollLocked: boolean;
}

/**
 * Collapsing the dock shrinks .app-main's bottom padding by --bottom-chrome-height
 * (56px), which reduces the document height. When the user is within that delta
 * of the bottom, the browser clamps scrollY down and the state machine reads
 * that correction as an up-scroll and flips the dock back to expanded, stranding
 * the viewport short of the bottom with the chrome overlapping the last content.
 * canCollapse=false (no collapse headroom) keeps the dock expanded instead.
 */
export interface MobileBottomDockScrollOptions {
	canCollapse?: boolean;
}

export interface MobileBottomDockStateMachine {
	snapshot(): MobileBottomDockSnapshot;
	scrollTo(scrollY: number, exclusions: MobileBottomDockScrollExclusions, options?: MobileBottomDockScrollOptions): void;
	restoreExpanded(scrollY: number): void;
	restoreDockState(dockState: Exclude<MobileBottomDockState, 'now-playing'>, scrollY: number): void;
	openNowPlaying(): void;
	closeNowPlaying(scrollY: number): void;
	setProgressDragging(active: boolean, scrollY: number): void;
	setVideoFullscreen(active: boolean, scrollY: number): void;
	rebase(scrollY: number): void;
}

export function createMobileBottomDockStateMachine(initialScrollY = 0): MobileBottomDockStateMachine {
	let dockState: MobileBottomDockState = 'expanded';
	let returnState: Exclude<MobileBottomDockState, 'now-playing'> = 'expanded';
	let previousScrollY = Math.max(0, initialScrollY);
	let downDistance = 0;
	let upDistance = 0;
	let progressDragging = false;
	let videoFullscreen = false;
	let needsBaseline = false;

	const resetDistances = () => {
		downDistance = 0;
		upDistance = 0;
	};
	const rebase = (scrollY: number) => {
		previousScrollY = Math.max(0, scrollY);
		resetDistances();
		needsBaseline = false;
	};
	const isExcluded = (exclusions: MobileBottomDockScrollExclusions) =>
		exclusions.modalActive || exclusions.editableFocused || progressDragging || videoFullscreen || dockState === 'now-playing';

		return {
			snapshot: () => ({ dockState, hidden: videoFullscreen, scrollLocked: dockState === 'now-playing' }),
			scrollTo(scrollY, exclusions, options) {
			const nextScrollY = Math.max(0, scrollY);
			if (isExcluded(exclusions)) {
				needsBaseline = true;
				return;
			}
			if (needsBaseline) {
				rebase(nextScrollY);
				return;
			}
			if (nextScrollY <= 0) {
				dockState = 'expanded';
				rebase(0);
				return;
			}
			const delta = nextScrollY - previousScrollY;
			previousScrollY = nextScrollY;
			if (delta > 0) {
				upDistance = 0;
				downDistance += delta;
				if (downDistance >= MOBILE_BOTTOM_DOCK_THRESHOLDS.compactAfterDownPx && (options?.canCollapse ?? true)) {
					dockState = 'compact';
					resetDistances();
				}
			} else if (delta < 0) {
				downDistance = 0;
				upDistance += -delta;
				if (upDistance >= MOBILE_BOTTOM_DOCK_THRESHOLDS.expandAfterUpPx) {
					dockState = 'expanded';
					resetDistances();
				}
			}
		},
		restoreExpanded(scrollY) {
			dockState = 'expanded';
			rebase(scrollY);
		},
		restoreDockState(nextDockState, scrollY) {
			dockState = nextDockState;
			returnState = nextDockState;
			rebase(scrollY);
		},
		openNowPlaying() {
			if (dockState === 'now-playing') return;
			returnState = dockState;
			dockState = 'now-playing';
			needsBaseline = true;
		},
		closeNowPlaying(scrollY) {
			if (dockState !== 'now-playing') return;
			dockState = returnState;
			rebase(scrollY);
		},
		setProgressDragging(active, scrollY) {
			progressDragging = active;
			if (!active) rebase(scrollY);
			else needsBaseline = true;
		},
		setVideoFullscreen(active, scrollY) {
			videoFullscreen = active;
			if (!active) rebase(scrollY);
			else needsBaseline = true;
		},
		rebase,
	};
}
